import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  IconButton,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import Grid from "@mui/material/Grid2";
import Overlay from "components/Overlay/Overlay";
import SaveRoundedIcon from "@mui/icons-material/SaveRounded";
import { getAuth } from "firebase/auth";
import { useStateContext } from "contexts/ContextProvider";
import { usePageTitle } from "hooks/usePageTitle";
import api from "services/api";

const getRequirementValueForTemplate = (linguist, template) => {
  const requirementColumn = String(template?.LinkedRequirementColumn || "").trim();
  if (!requirementColumn) return null;

  const mapValue = linguist?.RequirementMap?.[requirementColumn];
  if (mapValue === true || mapValue === false) return mapValue;

  const directValue = linguist?.[requirementColumn];
  if (directValue === true || directValue === false) return directValue;

  return null;
};

const getRequirementStatusLabel = (value) => {
  if (value === true) return "True";
  if (value === false) return "False";
  return "N/A";
};

const statusColorSx = (status) => {
  if (status === "success") return { color: "success.main" };
  if (status === "failed") return { color: "error.main" };
  if (status === "processing") return { color: "warning.main" };
  if (status === "queued") return { color: "info.main" };
  return { color: "text.secondary" };
};

const wait = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

const formatDuration = (durationMs) => {
  const normalized = Number(durationMs || 0);
  if (!Number.isFinite(normalized) || normalized <= 0) return "0ms";

  if (normalized < 1000) {
    return `${Math.max(1, Math.round(normalized))}ms`;
  }

  const totalSeconds = normalized / 1000;
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;

  if (minutes > 0) {
    const wholeSeconds = Math.floor(seconds);
    const millis = Math.round((seconds - wholeSeconds) * 1000);
    if (millis > 0) return `${minutes}m ${wholeSeconds}.${String(millis).padStart(3, "0")}s`;
    return `${minutes}m ${wholeSeconds}s`;
  }

  return `${totalSeconds.toFixed(3)}s`;
};

const compareTemplateOrder = (left, right) => {
  const leftSort = Number(left?.SortOrder || 0);
  const rightSort = Number(right?.SortOrder || 0);
  if (leftSort !== rightSort) return leftSort - rightSort;
  return Number(left?.id || 0) - Number(right?.id || 0);
};

const LinguistDocumentGeneration = () => {
  usePageTitle("Generate Linguist Documents");

  const theme = useTheme();
  const isMobile = !useMediaQuery("(min-width:600px)");

  const { overlayData, setOverlayName, showSnackbar } = useStateContext();

  const selectedLinguists = useMemo(() => {
    const fromArray = Array.isArray(overlayData?.Linguists)
      ? overlayData.Linguists.filter((linguistItem) => Number(linguistItem?.id || 0) > 0)
      : [];

    if (fromArray.length > 0) return fromArray;

    const singleLinguist = overlayData?.Linguist;
    const singleLinguistId = Number(overlayData?.LinguistID || singleLinguist?.id || 0);

    if (singleLinguistId > 0) {
      return [
        {
          ...(singleLinguist || {}),
          id: singleLinguistId,
        },
      ];
    }

    return [];
  }, [overlayData]);

  const linguistIds = useMemo(
    () => selectedLinguists.map((linguistItem) => Number(linguistItem.id)).filter((id) => id > 0),
    [selectedLinguists],
  );

  const isMultiLinguist = selectedLinguists.length > 1;

  const [loadingTemplates, setLoadingTemplates] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [selectedTemplateIdsByLinguistId, setSelectedTemplateIdsByLinguistId] = useState({});
  const [isGenerating, setIsGenerating] = useState(false);
  const mergePdfs = true;
  const [generationStatusByLinguistId, setGenerationStatusByLinguistId] = useState({});
  const [linguistTimingById, setLinguistTimingById] = useState({});
  const [totalGenerationStartedAt, setTotalGenerationStartedAt] = useState(null);
  const [totalGenerationCompletedAt, setTotalGenerationCompletedAt] = useState(null);
  const [generationNow, setGenerationNow] = useState(Date.now());
  const [currentLinguistName, setCurrentLinguistName] = useState("");
  const [linguistProgress, setLinguistProgress] = useState({
    done: 0,
    total: 0,
    success: 0,
    failed: 0,
  });
  const itemTimingRef = useRef({});
  const generationInFlightRef = useRef(false);

  const closeOverlay = () => setOverlayName("");

  useEffect(() => {
    let mounted = true;

    const fetchTemplates = async () => {
      setLoadingTemplates(true);
      try {
        const response = await api.get("/documentTemplates");
        if (!mounted) return;

        const availableTemplates = (response.data || []).filter(
          (template) => template && template.IsActive !== false,
        );

        setTemplates(
          [...availableTemplates].sort(compareTemplateOrder),
        );
      } catch (error) {
        if (mounted) {
          showSnackbar("Unable to load document templates", theme, "error");
        }
      } finally {
        if (mounted) setLoadingTemplates(false);
      }
    };

    fetchTemplates();

    return () => {
      mounted = false;
    };
  }, [showSnackbar, theme]);

  useEffect(() => {
    setSelectedTemplateIdsByLinguistId((previous) => {
      const templateIds = templates.map((template) => template.id);
      const next = {};

      selectedLinguists.forEach((linguist) => {
        const previousSelected = previous[linguist.id] || [];
        const validPrevious = previousSelected.filter((id) => templateIds.includes(id));
        next[linguist.id] = validPrevious.length > 0 ? validPrevious : [...templateIds];
      });

      return next;
    });
  }, [selectedLinguists, templates]);

  useEffect(() => {
    if (!isGenerating) return undefined;

    const intervalId = window.setInterval(() => {
      setGenerationNow(Date.now());
    }, 500);

    return () => window.clearInterval(intervalId);
  }, [isGenerating]);

  const totalGenerationDurationMs = useMemo(() => {
    if (!totalGenerationStartedAt) return 0;
    const end = totalGenerationCompletedAt || generationNow;
    return Math.max(0, end - totalGenerationStartedAt);
  }, [totalGenerationStartedAt, totalGenerationCompletedAt, generationNow]);

  const linguistProgressPercent = useMemo(() => {
    if (!linguistProgress.total) return 0;
    return Math.round((linguistProgress.done / linguistProgress.total) * 100);
  }, [linguistProgress]);

  const getSelectedTemplateIdsForLinguist = useCallback(
    (linguistId) => selectedTemplateIdsByLinguistId[linguistId] || [],
    [selectedTemplateIdsByLinguistId],
  );

  const hasAnySelectedTemplates = useMemo(
    () => selectedLinguists.some((linguist) => getSelectedTemplateIdsForLinguist(linguist.id).length > 0),
    [getSelectedTemplateIdsForLinguist, selectedLinguists],
  );

  const toggleSelection = (linguistId, templateId, checked) => {
    setSelectedTemplateIdsByLinguistId((previous) => {
      const current = previous[linguistId] || [];
      if (checked) {
        if (current.includes(templateId)) return previous;
        return {
          ...previous,
          [linguistId]: [...current, templateId],
        };
      }

      return {
        ...previous,
        [linguistId]: current.filter((id) => id !== templateId),
      };
    });
  };

  const toggleAllSelectionsForLinguist = useCallback(
    (linguistId, checked) => {
      const allTemplateIds = templates.map((template) => template.id);

      setSelectedTemplateIdsByLinguistId((previous) => ({
        ...previous,
        [linguistId]: checked ? allTemplateIds : [],
      }));
    },
    [templates],
  );

  const initializeGenerationStatus = (linguistId, ids) => {
    const next = {};
    ids.forEach((id) => {
      next[id] = {
        status: "queued",
        message: "Queued",
        durationMs: null,
      };
    });
    itemTimingRef.current[linguistId] = {};
    setGenerationStatusByLinguistId((previous) => ({
      ...previous,
      [linguistId]: next,
    }));
  };

  const syncStatusesFromJob = (linguistId, jobItems = []) => {
    const now = Date.now();
    const timingByTemplate = itemTimingRef.current[linguistId] || {};
    itemTimingRef.current[linguistId] = timingByTemplate;

    setGenerationStatusByLinguistId((previous) => {
      const previousLinguistStatuses = previous[linguistId] || {};
      const next = {};

      jobItems.forEach((item) => {
        const templateId = Number(item?.templateId || 0);
        if (!templateId) return;

        const status = String(item?.status || "queued").toLowerCase();
        const serverDurationMs =
          typeof item?.durationMs === "number" && Number.isFinite(item.durationMs)
            ? Math.max(0, item.durationMs)
            : null;

        if (status === "processing" && !timingByTemplate[templateId]?.startedAt) {
          timingByTemplate[templateId] = {
            ...(timingByTemplate[templateId] || {}),
            startedAt: now,
          };
        }

        if ((status === "success" || status === "failed") && !timingByTemplate[templateId]?.completedAt) {
          const startedAt = timingByTemplate[templateId]?.startedAt || now;
          timingByTemplate[templateId] = {
            ...(timingByTemplate[templateId] || {}),
            startedAt,
            completedAt: now,
            durationMs: Math.max(0, now - startedAt),
          };
        }

        const startedAt = timingByTemplate[templateId]?.startedAt || null;
        const liveDurationMs =
          status === "processing" && startedAt
            ? Math.max(0, now - startedAt)
            : null;

        const resolvedDurationMs =
          serverDurationMs ??
          timingByTemplate[templateId]?.durationMs ??
          liveDurationMs ??
          previousLinguistStatuses[templateId]?.durationMs ??
          null;

        next[templateId] = {
          status,
          message: item?.message || "Queued",
          durationMs: resolvedDurationMs,
        };
      });

      return {
        ...previous,
        [linguistId]: next,
      };
    });
  };

  const pollGenerationJob = async (jobId, linguistId) => {
    let attempts = 0;
    const maxAttempts = 300;

    while (attempts < maxAttempts) {
      attempts += 1;

      const response = await api.get(`/documentTemplates/generate/jobs/${jobId}`);
      const job = response?.data || {};
      const jobItems = Array.isArray(job.items) ? job.items : [];
      syncStatusesFromJob(linguistId, jobItems);

      const status = String(job.status || "").toLowerCase();
      if (status === "completed" || status === "failed") {
        return job;
      }

      await wait(1000);
    }

    throw new Error("Generation job polling timed out");
  };

  const downloadGeneratedDocuments = async (jobItems = []) => {
    const successfulItems = jobItems.filter(
      (item) => item?.status === "success" && Number(item?.generatedDocumentId || 0) > 0,
    );

    const uniqueItems = [];
    const seenGeneratedDocumentIds = new Set();
    successfulItems.forEach((item) => {
      const generatedDocumentId = Number(item?.generatedDocumentId || 0);
      if (!generatedDocumentId || seenGeneratedDocumentIds.has(generatedDocumentId)) return;
      seenGeneratedDocumentIds.add(generatedDocumentId);
      uniqueItems.push(item);
    });

    const auth = getAuth();
    const user = auth.currentUser;
    const token = user ? await user.getIdToken() : null;
    const apiBaseUrl = String(api?.defaults?.baseURL || "").replace(/\/$/, "");

    for (const item of uniqueItems) {
      const generatedDocumentId = Number(item.generatedDocumentId);
      const downloadUrl = `${apiBaseUrl}/documentTemplates/generated/${generatedDocumentId}/file?mode=download`;
      const response = await fetch(downloadUrl, {
        method: "GET",
        headers: {
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
      });

      if (!response.ok) {
        throw new Error(`Failed to download generated document ${generatedDocumentId}`);
      }

      const responseBlob = await response.blob();

      const fallbackName = String(item?.outputFileName || `document_${generatedDocumentId}.pdf`);
      const contentDisposition = response.headers.get("content-disposition") || "";
      const fileNameMatch = contentDisposition.match(/filename="?([^";]+)"?/i);
      const fileName = fileNameMatch?.[1] || fallbackName;

      const blobUrl = window.URL.createObjectURL(responseBlob);
      const link = document.createElement("a");
      link.href = blobUrl;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(blobUrl);
    }

    return uniqueItems.length;
  };

  const handleGenerate = async () => {
    if (!linguistIds.length || !hasAnySelectedTemplates || isGenerating || generationInFlightRef.current) {
      return;
    }

    generationInFlightRef.current = true;

    setIsGenerating(true);
    setCurrentLinguistName("");
    setGenerationStatusByLinguistId({});
    setLinguistTimingById({});
    setGenerationNow(Date.now());
    setTotalGenerationStartedAt(Date.now());
    setTotalGenerationCompletedAt(null);
    setLinguistProgress({
      done: 0,
      total: selectedLinguists.length,
      success: 0,
      failed: 0,
    });

    try {
      let completedLinguists = 0;
      let failedLinguists = 0;
      let downloadedFilesTotal = 0;
      let successDocumentsTotal = 0;
      let failedDocumentsTotal = 0;

      const updateLinguistProgress = ({
        done,
        success,
        failed,
      }) => {
        setLinguistProgress({
          done,
          total: selectedLinguists.length,
          success,
          failed,
        });
      };

      for (const linguist of selectedLinguists) {
        const linguistId = Number(linguist?.id || 0);
        const selectedTemplateIds = getSelectedTemplateIdsForLinguist(linguistId);

        const linguistStartedAt = Date.now();

        if (!linguistId) {
          failedLinguists += 1;
          updateLinguistProgress({
            done: completedLinguists + failedLinguists,
            success: completedLinguists,
            failed: failedLinguists,
          });
          continue;
        }

        if (!selectedTemplateIds.length) {
          completedLinguists += 1;
          setLinguistTimingById((previous) => ({
            ...previous,
            [linguistId]: {
              totalDurationMs: 0,
              completedTemplateCount: 0,
            },
          }));
          updateLinguistProgress({
            done: completedLinguists + failedLinguists,
            success: completedLinguists,
            failed: failedLinguists,
          });
          continue;
        }

        setCurrentLinguistName(String(linguist?.Name || `Linguist #${linguistId}`));
        initializeGenerationStatus(linguistId, selectedTemplateIds);

        try {
          const startResponse = await api.post(`/documentTemplates/generate/linguist/${linguistId}`, {
            templateIds: selectedTemplateIds,
            mergePdfs,
          });
          const jobId = startResponse?.data?.jobId;

          if (!jobId) {
            throw new Error("Unable to start generation job");
          }

          syncStatusesFromJob(linguistId, startResponse?.data?.items || []);

          const finalJob = await pollGenerationJob(jobId, linguistId);
          const successCount = Number(finalJob?.successCount || 0);
          const failedCount = Number(finalJob?.failedCount || 0);
          const downloadedCount = await downloadGeneratedDocuments(finalJob?.items || []);

          setLinguistTimingById((previous) => ({
            ...previous,
            [linguistId]: {
              totalDurationMs: Math.max(0, Date.now() - linguistStartedAt),
              completedTemplateCount: successCount + failedCount,
            },
          }));

          successDocumentsTotal += successCount;
          failedDocumentsTotal += failedCount;
          downloadedFilesTotal += downloadedCount;

          if (successCount > 0) {
            completedLinguists += 1;
          } else {
            failedLinguists += 1;
          }

          updateLinguistProgress({
            done: completedLinguists + failedLinguists,
            success: completedLinguists,
            failed: failedLinguists,
          });
        } catch (error) {
          failedLinguists += 1;
          setLinguistTimingById((previous) => ({
            ...previous,
            [linguistId]: {
              totalDurationMs: Math.max(0, Date.now() - linguistStartedAt),
              completedTemplateCount: 0,
            },
          }));
          updateLinguistProgress({
            done: completedLinguists + failedLinguists,
            success: completedLinguists,
            failed: failedLinguists,
          });
          console.error("Linguist generation failed:", error);
        }
      }

      if (completedLinguists > 0 && failedLinguists === 0) {
        showSnackbar(
          `Generated documents for ${completedLinguists} linguist(s). Downloaded ${downloadedFilesTotal} file(s).`,
          theme,
          "success",
        );
      } else if (completedLinguists > 0 && failedLinguists > 0) {
        showSnackbar(
          `Generation completed with partial failures (${completedLinguists} linguist(s) succeeded, ${failedLinguists} failed). Downloaded ${downloadedFilesTotal} file(s).`,
          theme,
          "warning",
        );
      } else if (successDocumentsTotal > 0 || failedDocumentsTotal > 0) {
        showSnackbar(
          `No linguists completed successfully (${successDocumentsTotal} documents succeeded, ${failedDocumentsTotal} failed).`,
          theme,
          "error",
        );
      } else {
        showSnackbar("No documents were generated", theme, "error");
      }
    } catch (error) {
      const message =
        error?.response?.data?.error ||
        error?.message ||
        "Document generation failed";
      showSnackbar(message, theme, "error");
    } finally {
      setTotalGenerationCompletedAt(Date.now());
      setIsGenerating(false);
      setCurrentLinguistName("");
      generationInFlightRef.current = false;
    }
  };

  return (
    <Overlay
      id="linguist-document-generation"
      open={true}
      onClose={closeOverlay}
      title="Generate Documents"
      maxWidthPx={1200}
      footer={
        <Box
          sx={{
            display: "flex",
            justifyContent: "flex-end",
            alignItems: "center",
            width: "100%",
            flexDirection: "row",
            gap: 1,
          }}
        >
          {isMobile ? (
            <Tooltip title="Generate" placement="top">
              <Box component="span" sx={{ display: "inline-flex" }}>
                <IconButton
                  color="primary"
                  onClick={handleGenerate}
                  disabled={
                    isGenerating ||
                    !hasAnySelectedTemplates ||
                    selectedLinguists.length === 0 ||
                    loadingTemplates
                  }
                  sx={{
                    width: "40px",
                    height: "40px",
                    backgroundColor: theme.palette.primary.main,
                    borderRadius: "12px",
                    color: "white",
                    "&:hover": {
                      backgroundColor:
                        theme.palette.primary.buttonHoverSelected,
                    },
                    "&.Mui-disabled": {
                      backgroundColor: theme.palette.grey[400],
                      color: theme.palette.grey[200],
                    },
                  }}
                >
                  <SaveRoundedIcon />
                </IconButton>
              </Box>
            </Tooltip>
          ) : (
            <Button
              color="primary"
              variant="contained"
              size="large"
              endIcon={<SaveRoundedIcon />}
              onClick={handleGenerate}
              disabled={
                isGenerating ||
                !hasAnySelectedTemplates ||
                selectedLinguists.length === 0 ||
                loadingTemplates
              }
              sx={{ borderRadius: "12px" }}
            >
              Generate Selected Documents
            </Button>
          )}
        </Box>
      }
    >
      {/* Content scroll port (mobile review standard, 2026-10-01) — the
          padded port is the single scroller; the Overlay renders the shell. */}
      <Box
        sx={{
          position: "relative",
          flex: 1,
          minHeight: 0,
          width: "100%",
          height: "100%",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <Box
          sx={{
            p: { xs: "0.75rem", sm: "1rem 2.3rem 1rem 2.5rem" },
            width: "100%",
            height: "100%",
            overflowY: "auto",
            overflowX: "hidden",
          }}
        >
          <Grid container spacing={2}>
            <Grid size={{ xs: 12 }}>
              <Typography variant="h5" fontWeight={600}>
                {isMultiLinguist
                  ? `Linguists Selected: ${selectedLinguists.length}`
                  : `Linguist: ${selectedLinguists[0]?.Name || "-"}`}
              </Typography>
              {isMultiLinguist && (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                  {selectedLinguists
                    .map((linguist) => linguist?.Name)
                    .filter(Boolean)
                    .join(", ")}
                </Typography>
              )}
              {isGenerating && currentLinguistName && (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                  Processing: {currentLinguistName} ({linguistProgress.done + 1 > linguistProgress.total ? linguistProgress.total : linguistProgress.done + 1}/{linguistProgress.total || selectedLinguists.length})
                </Typography>
              )}
            </Grid>
            
            <Grid size={{ xs: 12 }}>
              <Box
                sx={{
                  border: `1px solid ${theme.palette.primary.border2}`,
                  borderRadius: "10px",
                  overflow: "hidden",
                }}
              >
                {loadingTemplates ? (
                  <Box sx={{ p: 3, display: "flex", justifyContent: "center" }}>
                    <CircularProgress />
                  </Box>
                ) : templates.length === 0 ? (
                  <Box sx={{ p: 2.5 }}>
                    <Typography variant="body2" color="text.secondary">
                      No templates available.
                    </Typography>
                  </Box>
                ) : (
                  <Box sx={{ p: 1.25 }}>
                    <Grid container spacing={1.25}>
                      {selectedLinguists.map((linguistItem) => {
                        const linguistId = Number(linguistItem?.id || 0);
                        const selectedTemplateIds = getSelectedTemplateIdsForLinguist(linguistId);
                        const templateStatuses = generationStatusByLinguistId[linguistId] || {};
                        const timingSummary = linguistTimingById[linguistId] || {};
                        const totalTemplateCount = templates.length;
                        const selectedCount = selectedTemplateIds.length;
                        const allSelected = totalTemplateCount > 0 && selectedCount === totalTemplateCount;
                        const partiallySelected = selectedCount > 0 && selectedCount < totalTemplateCount;

                        return (
                          <Grid key={`linguist-card-${linguistId}`} size={{ xs: 12, md: 6, lg: 4 }}>
                            <Box
                              sx={{
                                border: `1px solid ${theme.palette.primary.border2}`,
                                borderRadius: "12px",
                                p: 1,
                                bgcolor: theme.palette.background.default,
                                height: "100%",
                                width: "100%",
                                maxWidth: "100%",
                                overflow: "hidden",
                                boxSizing: "border-box",
                              }}
                            >
                              <Box sx={{ mb: 0.75, display: "flex", justifyContent: "space-between", gap: 1, alignItems: "flex-start" }}>
                                <Box sx={{ minWidth: 0, flex: 1 }}>
                                  <Typography variant="body1" fontWeight={700} noWrap>
                                    {linguistItem?.Name || `Linguist #${linguistId}`}
                                  </Typography>
                                  <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
                                    Selected: {selectedTemplateIds.length} document(s)
                                  </Typography>
                                  <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
                                    Total time: {formatDuration(timingSummary.totalDurationMs)}
                                  </Typography>
                                </Box>

                                <Tooltip
                                  title={allSelected ? "Select none" : "Select all"}
                                  placement="top"
                                >
                                  <span>
                                    <Checkbox
                                      size="small"
                                      checked={allSelected}
                                      indeterminate={partiallySelected}
                                      disabled={isGenerating || totalTemplateCount === 0}
                                      onChange={(event) =>
                                        toggleAllSelectionsForLinguist(
                                          linguistId,
                                          event.target.checked,
                                        )
                                      }
                                      inputProps={{
                                        "aria-label": `Select all templates for ${linguistItem?.Name || `Linguist ${linguistId}`}`,
                                      }}
                                      sx={{ mt: -0.5, mr: -0.5 }}
                                    />
                                  </span>
                                </Tooltip>
                              </Box>

                              <Box sx={{ display: "grid", gap: 0.65, width: "100%", minWidth: 0 }}>
                                {templates.map((template) => {
                                  const requirementValue = getRequirementValueForTemplate(linguistItem, template);
                                  const statusText = getRequirementStatusLabel(requirementValue);
                                  const isSelected = selectedTemplateIds.includes(template.id);
                                  const templateProgress = templateStatuses[template.id];

                                  return (
                                    <Box
                                      key={`template-${linguistId}-${template.id}`}
                                      role="button"
                                      tabIndex={0}
                                      onClick={() => {
                                        if (isGenerating) return;
                                        toggleSelection(linguistId, template.id, !isSelected);
                                      }}
                                      onKeyDown={(event) => {
                                        if (isGenerating) return;
                                        if (event.key === "Enter" || event.key === " ") {
                                          event.preventDefault();
                                          toggleSelection(linguistId, template.id, !isSelected);
                                        }
                                      }}
                                      sx={{
                                        display: "flex",
                                        flexDirection: "column",
                                        gap: 0.4,
                                        p: 0.85,
                                        width: "100%",
                                        maxWidth: "100%",
                                        minWidth: 0,
                                        boxSizing: "border-box",
                                        borderRadius: "10px",
                                        border: `1px solid ${isSelected ? theme.palette.primary.main : theme.palette.primary.border2}`,
                                        bgcolor: isSelected
                                          ? theme.palette.action.selected
                                          : theme.palette.background.paper,
                                        cursor: isGenerating ? "not-allowed" : "pointer",
                                        opacity: isGenerating ? 0.8 : 1,
                                      }}
                                    >
                                      <Box sx={{ display: "flex", justifyContent: "space-between", gap: 1 }}>
                                        <Typography variant="body2" fontWeight={700} noWrap sx={{ minWidth: 0 }}>
                                          {template.Name || "Unnamed Template"}
                                        </Typography>
                                        <Checkbox
                                          checked={isSelected}
                                          disabled={isGenerating}
                                          onClick={(event) => event.stopPropagation()}
                                          onChange={(event) =>
                                            toggleSelection(linguistId, template.id, event.target.checked)
                                          }
                                        />
                                      </Box>

                                      <Typography
                                        variant="caption"
                                        color="text.secondary"
                                        sx={{ whiteSpace: "normal", overflowWrap: "anywhere" }}
                                      >
                                        Req: {template.LinkedRequirementColumn || "None"} | Value: {statusText}
                                      </Typography>

                                      <Typography
                                        variant="caption"
                                        sx={{
                                          ...statusColorSx(templateProgress?.status),
                                          whiteSpace: "normal",
                                          overflowWrap: "anywhere",
                                        }}
                                      >
                                        {(templateProgress?.message || "Not started")}
                                        {templateProgress?.durationMs !== null &&
                                        typeof templateProgress?.durationMs !== "undefined"
                                          ? ` • ${formatDuration(templateProgress.durationMs)}`
                                          : ""}
                                      </Typography>
                                    </Box>
                                  );
                                })}
                              </Box>
                            </Box>
                          </Grid>
                        );
                      })}
                    </Grid>
                  </Box>
                )}
              </Box>
            </Grid>

            {(isGenerating || linguistProgress.total > 0) && (
              <Grid size={{ xs: 12 }}>
                <Box
                  sx={{
                    border: `1px solid ${theme.palette.primary.border2}`,
                    borderRadius: "10px",
                    p: 1.5,
                    bgcolor: theme.palette.background.default,
                  }}
                >
                  <Box sx={{ display: "flex", justifyContent: "space-between", mb: 0.75 }}>
                    <Typography variant="body2" fontWeight={600}>
                      Linguist Progress
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {linguistProgress.done}/{linguistProgress.total}
                    </Typography>
                  </Box>
                  <Box
                    sx={{
                      width: "100%",
                      height: 10,
                      borderRadius: 5,
                      bgcolor: theme.palette.action.hover,
                      overflow: "hidden",
                    }}
                  >
                    <Box
                      sx={{
                        width: `${linguistProgressPercent}%`,
                        height: "100%",
                        transition: "width 0.2s ease",
                        bgcolor: theme.palette.primary.main,
                      }}
                    />
                  </Box>
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: "block" }}>
                    Success: {linguistProgress.success} | Failed: {linguistProgress.failed}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 0.25, display: "block" }}>
                    Total generation time: {formatDuration(totalGenerationDurationMs)}
                  </Typography>
                </Box>
              </Grid>
            )}

          </Grid>
        </Box>
      </Box>
    </Overlay>
  );
};

export default LinguistDocumentGeneration;
